import {
    useMemo,
    type FC
} from "react";
import {
    View
} from "react-native";
import type IInputGroupAddonProps from "./type";
import stylesheet, {
    INPUT_GROUP_KBD_SIZES,
    useStyles
} from "./stylesheet";
import {
    getInputGroupAddonIsTrailingTextAction,
    getInputGroupAddonToolbarGroups,
    getInputGroupAddonChildren
} from "./util";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    InputGroupAddonContext,
    useInputGroupContext
} from "../../context";
import {
    getInputGroupIconColor
} from "../../stylesheet";
import Text from "../../../text";

const InputGroupAddon: FC<IInputGroupAddonProps> = ({
    icon: IconComponentProp,
    variant = "inline",
    align = "start",
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        customTheme: groupCustomTheme,
        isCounterWarning,
        isCounterFull,
        isCounterOver,
        counterText,
        currentSize,
        isDisabled,
        counterID,
        size
    } = useInputGroupContext();

    const currentTheme = customTheme ?? groupCustomTheme;

    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const pointerType = usePointerType();

    const kbdSize = INPUT_GROUP_KBD_SIZES[size];

    const {
        container: containerDynamicStyle
    } = useStyles({
        isTrailingTextAction: getInputGroupAddonIsTrailingTextAction({
            children
        }),
        controlHeights,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        isDisabled,
        radiuses,
        borders,
        variant,
        colors,
        spaces,
        align
    });

    const renderIcon = () => {
        if(!IconComponentProp) {
            return null;
        }

        return <IconComponentProp
            customColor={getInputGroupIconColor({
                isDisabled,
                colors
            })}
            size={currentSize.iconSize}
            color="mid"
        />;
    };

    const addonContext = useMemo(() => ({
        variant,
        align
    }), [
        variant,
        align
    ]);

    const getCounterColor = () => {
        if(isDisabled) {
            return colors.system.state.content.disabled.neutral;
        }

        if(isCounterOver || isCounterFull) {
            return colors.content.text.danger;
        }

        return isCounterWarning ? colors.content.text.warning : colors.content.text.low;
    };

    const renderCounter = () => {
        if(!counterText) {
            return null;
        }

        return <Text
            customColor={getCounterColor()}
            style={stylesheet.counter}
            customTheme={currentTheme}
            variant="bodyMediumSize"
            aria-live="polite"
            id={counterID}
        >
            {counterText}
        </Text>;
    };

    const renderToolbar = () => {
        const groups = getInputGroupAddonToolbarGroups({
            isDisabled,
            children,
            kbdSize
        });

        return <>
            {groups.leading}
            <View
                style={stylesheet.spacer}
            />
            {groups.kbds}
            {renderCounter()}
            {groups.trailing}
        </>;
    };

    const renderContent = () => {
        if(align === "blockEnd") {
            return renderToolbar();
        }

        return <>
            {renderIcon()}
            {
                getInputGroupAddonChildren({
                    isDisabled,
                    children,
                    kbdSize
                })
            }
        </>;
    };

    return <InputGroupAddonContext.Provider
        value={addonContext}
    >
        <View
            {...props}
            style={[
                style,
                containerDynamicStyle
            ]}
        >
            {renderContent()}
        </View>
    </InputGroupAddonContext.Provider>;
};
export default InputGroupAddon;
